"use client";

import { HTMLAttributes, ReactNode } from "react";
import { MoreHorizontal } from "lucide-react";
import { StickyNote, StickyColor } from "./StickyNote";
import { PropertyBadge, BadgeColor } from "./PropertyBadge";
import { IconButton } from "./IconButton";

interface StickyCardProps extends HTMLAttributes<HTMLDivElement> {
  color?: StickyColor;
  title?: string;
  subtitle?: string;
  rotate?: string;
  showMenu?: boolean;
  onMenuClick?: () => void;
  /** Property badges to display (status, tags, etc.) */
  badges?: Array<{ label: string; color?: BadgeColor }>;
  /** Size variant */
  size?: "sm" | "md" | "lg";
  children?: ReactNode;
}

export function StickyCard({
  color = "yellow",
  title,
  subtitle,
  rotate,
  showMenu = false,
  onMenuClick,
  badges,
  size = "md",
  className = "",
  children,
  ...props
}: StickyCardProps) {
  return (
    <StickyNote
      color={color}
      rotate={rotate}
      size={size}
      className={`group ${className}`}
      {...props}
    >
      {/* Menu Button */}
      {showMenu && (
        <IconButton
          icon={<MoreHorizontal className="w-4 h-4 opacity-60" />}
          label="More options"
          variant="ghost"
          size="sm"
          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 !bg-transparent hover:!bg-black/10"
          onClick={(e) => {
            e.stopPropagation();
            onMenuClick?.();
          }}
        />
      )}

      {/* Content */}
      <div className="space-y-2 pt-2">
        {title && (
          <h4 className="font-semibold text-sm leading-snug line-clamp-2">{title}</h4>
        )}
        {subtitle && (
          <p className="text-xs opacity-70 line-clamp-2">{subtitle}</p>
        )}

        {/* Property Badges */}
        {badges && badges.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {badges.map((badge, index) => (
              <PropertyBadge
                key={index}
                label={badge.label}
                color={badge.color}
                size="sm"
              />
            ))}
          </div>
        )}

        {children}
      </div>
    </StickyNote>
  );
}

export type { StickyCardProps };
